import { useEffect, useRef, useState } from 'react';

interface UseVisibilityPollingOptions {
  /**
   * Function to call immediately when the tab becomes visible and `isActive` is true.
   * Useful for eager data refresh when the user returns to the tab.
   */
  onImmediate?: () => void | Promise<void>;

  /**
   * Function to call at the specified interval (in ms) while the tab is visible and `isActive` is true.
   * Useful for background data polling.
   */
  onInterval: () => void | Promise<void>;

  /**
   * External condition to determine whether polling should be active (e.g. current route).
   */
  isActive: boolean;

  /**
   * Interval duration in milliseconds for polling (default: 2 minutes).
   */
  intervalMs?: number;
}

/**
 * Hook that runs `onImmediate` once when the tab becomes visible (and `isActive` is true),
 * and then repeatedly runs `onInterval` on the given interval as long as the tab stays visible and `isActive`.
 *
 * Automatically cleans up the polling interval and restores it when conditions become valid again.
 *
 * @example
 * useVisibilityPolling({
 *   onImmediate: () => fetchData({force: true}),
 *   onInterval: () => fetchData({force: false}),
 *   isActive: location.pathname === "/admin-panel/payments",
 *   intervalMs: 120000, // 2 minutes
 * });
 */
export function useVisibilityPolling({
  onImmediate,
  onInterval,
  isActive,
  intervalMs = 2 * 60 * 1000,
}: UseVisibilityPollingOptions) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasRunImmediate = useRef(false);
  
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isTabVisible = document.visibilityState === 'visible';
  
      if (isTabVisible && isActive) {
        if (intervalRef.current === null) {
          intervalRef.current = setInterval(() => {
            onInterval();
          }, intervalMs);
  
          if (!hasRunImmediate.current && onImmediate) {
            onImmediate();
            hasRunImmediate.current = true;
          }
        }
      } else {
        if (intervalRef.current !== null) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        hasRunImmediate.current = false;
      }
    };
  
    document.addEventListener('visibilitychange', handleVisibilityChange);
    handleVisibilityChange(); // Trigger on mount
  
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [onImmediate, onInterval, isActive, intervalMs]);
}
